import ivclass from '@invideoio/web-shared/Modules/IVClass';
import { ImageKitWrapper } from '@invideoio/web-shared/components/ImageKitWrapper/ImageKitWrapper';
import PlayVideoOnHover from '@invideoio/web-shared/components/PlayVideoOnHover';
import { CARD_HEIGHT_MAP } from '@invideoio/web-shared/constants/templateGrid';
import { AssetsAppName, ExpandIcon, getWebAssetsUrl } from '@utils/web-assets';
import { useContext, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { StateContext } from '@store/store';

import { PlatformType } from '@invideoio/web-shared/constants/common';
import {
  LIHPTemplate,
  TemplateDimension,
  TemplateSearchWorkflow,
} from '@invideoio/web-shared/types/Templates';
import { CTAInvideoAiPromptPage } from '@constants/makepage';
import { VideoWallTemplateBasic } from '@components/templates/types';

export interface TemplateTileProps {
  template: VideoWallTemplateBasic;

  workflow: TemplateSearchWorkflow;
  onTemplateSelect: (
    template?: LIHPTemplate,
    dimension?: TemplateDimension,
    isRelatedTemplate?: boolean,
  ) => void;

  dimension?: TemplateDimension;
  isRelatedTemplate?: boolean;
  showFavourite?: boolean;
  isExclusive?: boolean;
}

const TemplateTile = ({
  template,
  isRelatedTemplate,
  onTemplateSelect,
  dimension: selectedDimension,
}: TemplateTileProps) => {
  const globalState = useContext(StateContext);
  const { platform } = globalState;
  const isDesktop = platform === PlatformType.Desktop;
  const dimension = selectedDimension || template.dimension;
  const componentRef = useRef<HTMLDivElement | null>(null);
  const [componentDimension, setComponentDimension] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const assetsUrl = process.env.NEXT_PUBLIC_EDITOR_CDN_URL;

  const [isMuted, setIsMuted] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const toggleIsHovered = () => {
    setIsHovered(!isHovered);
  };
  const toggleMute = () => {
    setIsMuted(!isMuted);
  };
  const openTemplatePreview = async () => {
    const lihpTemplate: LIHPTemplate = {
      ...template,
      dimensions: ['16:9'],
      isFavouriteTemplate: false,
      template_type: '',
      creator_id: 0,
      creator_name: '',
      workflow: '',
      categories: [],
      industries: [],
      templateURL: CTAInvideoAiPromptPage,
    };
    onTemplateSelect(lihpTemplate, template.dimension, isRelatedTemplate);
  };

  useEffect(() => {
    const updateDimension = () => {
      if (!componentRef.current) return;

      const { width, height } = componentRef.current.getBoundingClientRect();

      const hasChanged =
        width !== componentDimension?.width ||
        height !== componentDimension?.height;

      if (hasChanged) {
        setComponentDimension({ width, height });
      }
    };

    updateDimension();

    let timeOut: ReturnType<typeof setTimeout> | null;
    const resizeListener = () => {
      if (timeOut) clearTimeout(timeOut);
      timeOut = setTimeout(updateDimension, 1000);
    };

    window.addEventListener('resize', resizeListener);
    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, [componentDimension]);

  return (
    <div
      onMouseEnter={isDesktop ? toggleIsHovered : undefined}
      onMouseLeave={isDesktop ? toggleIsHovered : undefined}
      className={ivclass(
        'iv-relative iv-group',
        isDesktop ? 'iv-mb-[12px]' : 'iv-mb-[22px]',
      )}
      ref={componentRef}
    >
      <div className={ivclass('iv-relative')}>
        {isDesktop && isHovered ? (
          <button
            onClick={toggleMute}
            className={ivclass(
              'iv-absolute iv-top-[10px] iv-left-[10px]',
              'iv-opacity-30 hover:iv-opacity-100',
              'iv-bg-grey-0',
              'iv-border-solid iv-border-grey-85',
              'iv-rounded-full',
              'iv-w-[45px] iv-h-[45px]',
              'iv-flex iv-items-center iv-justify-center',
              'iv-shadow-md',
              'iv-cursor-pointer',
              'iv-transition-all iv-duration-500 iv-ease-in-out',
              'hover:iv-scale-100',
            )}
            style={{
              border: '5px solid rgba(133, 133, 133, 0.2)',
              zIndex: 100,
            }}
            onMouseOver={e => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.opacity = '0.3';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {isMuted ? (
              <Image
                width={20}
                height={20}
                onClick={toggleMute}
                src={
                  assetsUrl +
                  '/dark/panels/top-panel/component-props/selected/volume-mute.svg'
                }
                alt="Mute"
              />
            ) : (
              <Image
                width={20}
                height={20}
                onClick={toggleMute}
                src={
                  assetsUrl +
                  '/dark/panels/top-panel/component-props/selected/volume.svg'
                }
                alt="Unmute"
              />
            )}
          </button>
        ) : null}
        {isDesktop && (
          <img
            onClick={openTemplatePreview}
            src={getWebAssetsUrl(ExpandIcon, AssetsAppName.LIHP)}
            alt="expand-icon"
            className={ivclass(
              'iv-absolute iv-bottom-[6px] iv-left-[6px]',
              'iv-invisible group-hover:iv-visible iv-z-[2]',
            )}
          />
        )}

        <div
          className={ivclass('iv-flex', 'iv-justify-center', 'iv-items-center')}
          onClick={openTemplatePreview}
        >
          <div
            className={ivclass(
              'iv-relative',
              'iv-template-hover',
              'iv-rounded-template-card',
              'iv-overflow-hidden',
              'iv-bg-black-4',
              'iv-w-full',
              CARD_HEIGHT_MAP[dimension],
            )}
            id={'play-video-hover-container'}
          >
            {!componentDimension ? (
              <div
                className={ivclass(
                  'iv-absolute iv-inset-0',
                  'iv-animate-pulse',
                  'iv-bg-gradient-to-r',
                )}
                style={{
                  background:
                    'linear-gradient(to right, rgb(224, 224, 224) 0%, rgb(213, 213, 213) 20%, rgb(224, 224, 224) 40%, rgb(224, 224, 224) 100%)',
                  animation: 'shimmerAnimation 1s infinite',
                  backgroundSize: '200% 100%',
                }}
              />
            ) : (
              <div
                className={
                  'iv-absolute iv-top-0 iv-left-0 iv-right-0 iv-bottom-0 iv-w-full iv-h-full iv-cursor-pointer'
                }
              >
                {componentDimension &&
                  (isDesktop ? (
                    <PlayVideoOnHover
                      videoSrc={template.previewURL}
                      muted={isMuted}
                      pausedOverlay={
                        <div>
                          <ImageKitWrapper
                            thumbnailUrl={template.thumbnailURL}
                            width={componentDimension.width}
                            height={componentDimension.height}
                          />
                        </div>
                      }
                      loadingOverlay={
                        <div className="iv-relative">
                          <div className={ivclass(`progressBarIvShared`)} />
                          <ImageKitWrapper
                            thumbnailUrl={template.thumbnailURL}
                            width={componentDimension.width}
                            height={componentDimension.height}
                          />
                        </div>
                      }
                    />
                  ) : (
                    <ImageKitWrapper
                      thumbnailUrl={template.thumbnailURL}
                      width={componentDimension.width}
                      height={componentDimension.height}
                    />
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {isDesktop && (
        <div
          className={ivclass(
            'iv-text-body-m iv-font-medium iv-mt-[4px]',
            'iv-text-grey-60 group-hover:iv-text-grey-85 iv-cursor-default',
            'iv-overflow-hidden iv-whitespace-nowrap iv-text-ellipsis',
          )}
        >
          {template?.name || ''}
        </div>
      )}
    </div>
  );
};

export default TemplateTile;
